import { Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';

import { Button } from '../../design-system/button.js';
import { usePlaybackTransport } from '../../player/player-context.js';
import { useTrackRemoval } from './track-removal.js';

export function CurrentTrackRemovalButton({ className }: { className?: string }) {
  const player = usePlaybackTransport();
  const removal = useTrackRemoval(player.removeTracks);
  const requestPending = useRef(false);
  const [failedTrackId, setFailedTrackId] = useState<string | null>(null);
  const item = player.item;

  if (!item) return null;

  const failed = failedTrackId === item.id;
  const label = failed
    ? `Removal failed. Remove ${item.title} from Song Composer again`
    : `Remove ${item.title} from Song Composer`;

  const remove = async () => {
    if (requestPending.current || removal.isPending) return;
    requestPending.current = true;
    setFailedTrackId(null);
    try {
      await removal.removeTracks([item.id]);
    } catch (error) {
      console.error('Song Composer player track removal request failed.', {
        errorName: error instanceof Error ? error.name : 'UnknownError',
      });
      setFailedTrackId(item.id);
    } finally {
      requestPending.current = false;
    }
  };

  return (
    <>
      <Button
        aria-busy={removal.isPending || undefined}
        aria-label={label}
        className={className}
        disabled={removal.isPending}
        onClick={() => void remove()}
        size="icon"
        title={label}
        variant="ghost"
      >
        <Trash2 aria-hidden="true" className="size-5" />
      </Button>
      <span aria-live="assertive" className="sr-only" role="status">
        {failed ? `Could not remove ${item.title}. Try again.` : ''}
      </span>
    </>
  );
}
